import { getBrandName } from '@curology/ui-components-web/brand';
import { Progress } from '@curology/ui-components-web/react/Progress';
import type braintree from 'braintree-web';
import {
  type AuthorizationResponse,
  type ButtonStyle,
} from 'paypal-checkout-components';
import React, { useEffect, useRef } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { usePayPalClient } from '@/components/payments/hooks/usePayPalClient';
import { PAYPAL_CONSTANTS } from '@/consts/payments';

import { useBraintree } from '../hooks/useBraintree';

export const PaypalButton = ({
  onSuccess,
  onError,
  onRenderButton = () => {},
  createPaymentOptions,
  buttonStyleOptions,
}: {
  onSuccess: (
    token: AuthorizationResponse,
    deviceData: string,
  ) => Promise<void>;
  onError: (message: string) => void;
  onRenderButton?: () => void;
  createPaymentOptions?: Partial<braintree.PayPalCheckoutCreatePaymentOptions>;
  buttonStyleOptions?: Partial<ButtonStyle>;
}) => {
  const { brand } = useFlowContext();
  const payPalClient = usePayPalClient();
  const braintree = useBraintree();
  const hasRenderedRef = useRef(false);

  const paypalButtonContainerId = 'paypal-button-container';

  // Reset rendering state on unmount so the button can re-render on re-mount
  useEffect(() => {
    return () => {
      hasRenderedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadPaypalButton = async () => {
      if (!payPalClient?.Button || !braintree.clients) {
        return;
      }

      // Only render the PayPal button once per mount cycle to prevent
      // "Page already has an active payment session" errors
      if (hasRenderedRef.current) {
        return;
      }
      hasRenderedRef.current = true;

      onRenderButton();

      const { payPalCheckout, dataCollector } = braintree.clients;

      // Clear the paypal button container before rendering the button to avoid duplicate buttons
      const paypalButtonContainer = document.getElementById(
        paypalButtonContainerId,
      );
      if (paypalButtonContainer) {
        paypalButtonContainer.innerHTML = '';
      }

      payPalClient.Button.render(
        {
          env: braintree.env,
          style: {
            height: 55, // The height must be between 25px and 55px
            label: PAYPAL_CONSTANTS.PAYPAL,
            size: PAYPAL_CONSTANTS.RESPONSIVE,
            shape: PAYPAL_CONSTANTS.PILL,
            tagline: false,
            ...buttonStyleOptions,
          },
          onAuthorize: async (data) => {
            const authorizedPayment =
              await payPalCheckout.tokenizePayment(data);

            await onSuccess(authorizedPayment, dataCollector.deviceData);

            return authorizedPayment;
          },
          payment: async () => {
            return payPalCheckout.createPayment({
              displayName: getBrandName(brand),
              flow: PAYPAL_CONSTANTS.VAULT,
              intent: PAYPAL_CONSTANTS.AUTHORIZE,
              ...createPaymentOptions,
            });
          },
        },
        `#${paypalButtonContainerId}`,
      );
    };

    try {
      loadPaypalButton();
    } catch {
      onError(
        'There was an error loading PayPal. Please refresh and try again.',
      );
    }
  }, [
    payPalClient,
    braintree.clients,
    onRenderButton,
    onError,
    braintree.env,
    buttonStyleOptions,
    onSuccess,
    brand,
    createPaymentOptions,
  ]);

  return (
    <div className="cur-text-center cur-pt-4">
      {!payPalClient?.Button ||
        (!braintree.clients && (
          <Progress
            className="cur-inline-block"
            progressType="circle"
            state="highlight"
          />
        ))}
      <div id={paypalButtonContainerId} />
    </div>
  );
};
